/**
 * Vue Router 路由配置
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { showToast } from 'vant'
import tokenManager from '@/common/auth/tokenManager'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/home/index.vue'),
    meta: { title: '首页', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/favorites',
    name: 'Favorites',
    component: () => import('@/pages/favorites/index.vue'),
    meta: { title: '喜欢', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/food/:id',
    name: 'FoodDetail',
    component: () => import('@/pages/food/detail.vue'),
    meta: { title: '美食详情', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/article/:slug',
    name: 'ArticleDetail',
    component: () => import('@/pages/article/detail.vue'),
    meta: { title: '攻略详情', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/articles',
    name: 'ArticleList',
    component: () => import('@/pages/article/list.vue'),
    meta: { title: '攻略列表', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/login/index.vue'),
    meta: { title: '登录', requiresAuth: false, keepAlive: false, hideTabbar: true }
  },
  {
    path: '/login/cmi',
    name: 'CmiLogin',
    component: () => import('@/pages/login/cmi.vue'),
    meta: { title: '登录', requiresAuth: false, keepAlive: false, hideTabbar: true }
  },
  {
    path: '/camera',
    redirect: '/camera/multi'
  },
  {
    path: '/camera/multi',
    name: 'CameraMulti',
    component: () => import('@/pages/camera/multi.vue'),
    meta: { title: '多张拍摄', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/upload/stream',
    name: 'UploadStream',
    component: () => import('@/pages/upload/stream.vue'),
    meta: { title: '识别中', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/menu',
    name: 'Menu',
    component: () => import('@/pages/menu/index.vue'),
    meta: { title: '点餐', requiresAuth: true, keepAlive: true }
  },
  {
    path: '/dish-detail',
    name: 'DishDetail',
    component: () => import('@/pages/dish-detail/index.vue'),
    meta: { title: '菜品详情', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/ai-recommend/result',
    name: 'AIRecommendResult',
    component: () => import('@/pages/ai-recommend/result.vue'),
    meta: { title: 'AI推荐结果', requiresAuth: true, keepAlive: true }
  },
  {
    path: '/order-conversation',
    name: 'OrderConversation',
    component: () => import('@/pages/order-conversation/index.vue'),
    meta: { title: '点餐对话', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/pages/profile/index.vue'),
    meta: { title: '我的', requiresAuth: true, keepAlive: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()

  if (to.meta?.title) {
    document.title = `${to.meta.title} - AI智能点餐`
  }

  const whiteList = ['/login']
  if (whiteList.includes(to.path)) {
    return next()
  }

  if (!to.meta?.requiresAuth) {
    return next()
  }

  try {
    const authStatus = await tokenManager.checkAuthStatus()
    switch (authStatus) {
      case 'valid':
        next()
        break
      case 'need_refresh':
        try {
          await tokenManager.getValidAccessToken()
          next()
        } catch (error) {
          console.error('Token刷新失败:', error)
          showToast('登录已过期，请重新登录')
          next({ path: '/login', query: { redirect: to.fullPath } })
        }
        break
      case 'expired':
      case 'no_token':
        showToast('请先登录')
        next({ path: '/login', query: { redirect: to.fullPath } })
        break
      default:
        next({ path: '/login', query: { redirect: to.fullPath } })
    }
  } catch (error) {
    console.error('路由守卫错误:', error)
    userStore.clearUserData?.()
    next({ path: '/login', query: { redirect: to.fullPath } })
  }
})

router.onError((error) => {
  console.error('路由错误:', error)
  showToast('页面加载失败')
})

export default router
